'use client'

import Link from 'next/link'
import { useCallback, useEffect, useState } from 'react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Check, Copy, KeyRound, Loader2 } from '@/components/ui/icons'
import { buildCanvasPath } from '@/lib/canvasSlug'
import { captureSentryError } from '@/lib/sentry'

import {
  createGenerationApiKey,
  listGenerationApiKeys,
  revokeGenerationApiKey,
  type GenerationApiKeySummary,
} from './apiKeysClient'

interface GenerationApiKeysPanelProps {
  organizationId: string
  canCreate?: boolean
  canRevoke?: boolean
}

export function GenerationApiPageHeader() {
  return (
    <div>
      <h1 className="text-2xl font-semibold tracking-tight text-foreground">Martini API</h1>
      <p className="mt-1.5 text-sm text-muted-foreground">
        Create keys to submit generations and run workflows from your software. Both use olives; generations appear on
        the API Generations canvas, workflow runs in the Workflow runs project (or the project you name).
      </p>
    </div>
  )
}

function isExpired(key: { expiresAt?: string | null }): boolean {
  return Boolean(key.expiresAt && new Date(key.expiresAt).getTime() <= Date.now())
}

function formatDate(value: string | null): string {
  if (!value) return 'Never'
  return new Date(value).toLocaleString()
}

export function GenerationApiKeysPanel({
  organizationId,
  canCreate = true,
  canRevoke = true,
}: GenerationApiKeysPanelProps) {
  const [keys, setKeys] = useState<GenerationApiKeySummary[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [label, setLabel] = useState('')
  const [newSecret, setNewSecret] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [revokeTarget, setRevokeTarget] = useState<GenerationApiKeySummary | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Every live api/cli key pins the organization's current API Generations
  // project (the backend re-pins them when it rotates), so any one of them
  // is the link. Run keys point at their run's project instead.
  const apiGenerationsProjectId =
    keys.find(key => !key.revokedAt && !isExpired(key) && key.kind !== 'run')?.defaultProjectId ?? null

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setKeys(await listGenerationApiKeys(organizationId))
    } catch (loadError) {
      captureSentryError(loadError, {
        operation: 'load_generation_api_keys',
        component: 'GenerationApiKeysPanel',
      })
      setError(loadError instanceof Error ? loadError.message : 'Failed to load Martini API keys')
    } finally {
      setLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => void refresh(), 0)
    return () => window.clearTimeout(timeoutId)
  }, [refresh])

  const createKey = async () => {
    const normalized = label.trim()
    if (!normalized) return
    setSubmitting(true)
    setError(null)
    try {
      const created = await createGenerationApiKey(organizationId, normalized)
      setKeys(previous => [created.apiKey, ...previous])
      setNewSecret(created.key)
      setLabel('')
    } catch (createError) {
      captureSentryError(createError, {
        operation: 'create_generation_api_key',
        component: 'GenerationApiKeysPanel',
      })
      setError(createError instanceof Error ? createError.message : 'Failed to create Martini API key')
    } finally {
      setSubmitting(false)
    }
  }

  const revokeKey = async () => {
    if (!revokeTarget) return
    setSubmitting(true)
    setError(null)
    try {
      await revokeGenerationApiKey(organizationId, revokeTarget.id)
      setKeys(previous =>
        previous.map(key => (key.id === revokeTarget.id ? { ...key, revokedAt: new Date().toISOString() } : key)),
      )
      setRevokeTarget(null)
    } catch (revokeError) {
      captureSentryError(revokeError, {
        operation: 'revoke_generation_api_key',
        component: 'GenerationApiKeysPanel',
      })
      setError(revokeError instanceof Error ? revokeError.message : 'Failed to revoke Martini API key')
    } finally {
      setSubmitting(false)
    }
  }

  const copySecret = async () => {
    if (!newSecret) return
    await navigator.clipboard.writeText(newSecret)
    setCopied(true)
    window.setTimeout(() => setCopied(false), 1500)
  }

  return (
    <section className="space-y-3">
      <div className="rounded-lg border border-border bg-card">
        <div className="flex flex-wrap items-start justify-between gap-4 px-5 py-4">
          <div>
            <div className="flex items-center gap-2">
              <KeyRound className="size-4 text-muted-foreground" aria-hidden="true" />
              <h2 className="text-sm font-medium">API keys</h2>
            </div>
            <p className="mt-1 max-w-xl text-xs text-muted-foreground">
              Create and revoke keys for your Martini API clients.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button asChild variant="outline" size="sm">
              <Link href="/docs/api">API Docs</Link>
            </Button>
            <Button asChild variant="outline" size="sm">
              <Link href="/docs/workflows">Workflows API</Link>
            </Button>
          </div>
        </div>

        {canCreate ? (
          <div className="flex flex-col gap-2 border-t border-border px-5 py-4 sm:flex-row">
            <Input
              value={label}
              onChange={event => setLabel(event.target.value)}
              placeholder="e.g., Production renders"
              maxLength={80}
              disabled={submitting}
            />
            <Button onClick={() => void createKey()} disabled={submitting || !label.trim()}>
              {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              Create Key
            </Button>
          </div>
        ) : null}

        <div className="border-t border-border">
          {loading ? (
            <div className="flex items-center gap-2 px-5 py-6 text-xs text-muted-foreground">
              <Loader2 className="size-3 animate-spin" aria-hidden="true" />
              Loading keys...
            </div>
          ) : keys.length === 0 ? (
            <div className="px-5 py-8 text-center">
              <p className="text-xs text-muted-foreground">No Martini API keys</p>
              <p className="mt-1 text-[10px] text-muted-foreground/60">
                Create a key to submit generations or run workflows from your server.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {apiGenerationsProjectId && (
                <li className="flex items-center justify-between gap-4 px-5 py-2.5">
                  <p className="text-[11px] text-muted-foreground">
                    Generations submitted with these keys land in the API Generations project.
                  </p>
                  <Link
                    href={buildCanvasPath(apiGenerationsProjectId, undefined)}
                    className="shrink-0 text-[11px] text-foreground underline-offset-4 hover:underline"
                  >
                    Open API Generations
                  </Link>
                </li>
              )}
              {keys.map(key => (
                <li key={key.id} className="flex flex-wrap items-center justify-between gap-4 px-5 py-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm">{key.label}</span>
                      <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {key.revokedAt ? 'Revoked' : isExpired(key) ? 'Expired' : 'Active'}
                      </span>
                      {key.kind === 'cli' && (
                        <span className="rounded border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          CLI
                        </span>
                      )}
                    </div>
                    <code className="mt-1 block text-[11px] text-muted-foreground">
                      {key.prefix}••••{key.lastFour}
                    </code>
                    <p className="mt-1 text-[10px] text-muted-foreground/60">
                      Last used {formatDate(key.lastUsedAt)}
                      {key.deviceLabel ? ` · ${key.deviceLabel}` : ''}
                      {key.expiresAt && !key.revokedAt
                        ? ` · ${isExpired(key) ? 'Expired' : 'Expires'} ${formatDate(key.expiresAt)}`
                        : ''}
                    </p>
                  </div>
                  {canRevoke && !key.revokedAt ? (
                    <Button variant="outline" size="sm" disabled={submitting} onClick={() => setRevokeTarget(key)}>
                      Revoke
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {error ? <p className="text-xs text-destructive">{error}</p> : null}

      <Dialog open={newSecret !== null} onOpenChange={open => !open && setNewSecret(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Copy API Key</DialogTitle>
            <DialogDescription>
              This key is shown once. Store it in a server-side secret before closing this dialog.
            </DialogDescription>
          </DialogHeader>
          <div className="flex min-w-0 items-center gap-2">
            <code className="min-w-0 flex-1 overflow-x-auto rounded border border-border bg-secondary px-3 py-2 text-xs">
              {newSecret}
            </code>
            <Button variant="outline" size="sm" onClick={() => void copySecret()}>
              {copied ? <Check aria-hidden="true" /> : <Copy aria-hidden="true" />}
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <DialogFooter>
            <Button onClick={() => setNewSecret(null)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={revokeTarget !== null} onOpenChange={open => !open && setRevokeTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Revoke API Key</DialogTitle>
            <DialogDescription>
              Requests using {revokeTarget?.label ?? 'this key'} will stop immediately. Existing generation history
              remains available.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => void revokeKey()} disabled={submitting}>
              {submitting ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
              Revoke Key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  )
}
